import { BrowserFeaturesPlugin } from '@snowplow/browser-plugin-browser-features';
import { TimezonePlugin } from '@snowplow/browser-plugin-timezone';
import { trackPageView } from '@snowplow/browser-tracker';
import OptanonConsentCookies from '../../optanon_consent_cookies.json';
import { BOT_AGENTS } from '../constants';
import AdFraudDetection from '../identifiers/ad_fraud_detection';
import Experimentation from '../identifiers/experimentation';
import UserProfileService from '../identifiers/user_profile_service';
import UserTargeting from '../identifiers/user_targeting';
import { MetadataUtils } from '../types/metadata_utils';
import { SnowplowUtils } from '../types/snowplow_utils';
import { buildCamel } from './build_helper';
import { browserMetadataUtils } from './metadata_utils';

export class SnowplowHelper {
  private botAgents = BOT_AGENTS.join('|');
  private trackStructEventFn: any;
  private newTrackerFn: any;
  private tracker: any = null;

  constructor(
    snowplowUtils: SnowplowUtils,
    private readonly metadataUtils: MetadataUtils = browserMetadataUtils,
  ) {
    this.newTrackerFn = snowplowUtils.newTracker;
    this.trackStructEventFn = snowplowUtils.trackStructEvent;
  }

  forceHttps(url: string) {
    let newUrl = url;
    newUrl = newUrl.trim().replace(/\s/g, '');

    // Checking if the URL String contains any protocol by checking occurrence of '://'
    if (/^(:\/\/)/.test(newUrl)) {
      return `https${newUrl}`;
    }
    // If URL String contains any protocol other than HTTPS, we replace it with HTTPS.
    if (!/^(ht)tps:\/\//i.test(newUrl)) {
      newUrl = url.replace(/(^\w+:|^)\/\//, '');
      return `https://${newUrl}`;
    }

    return newUrl;
  }

  isBotUserAgent() {
    const userAgent = this.metadataUtils.getClientUserAgent();
    const botRegex = new RegExp(`(${this.botAgents})`, 'i');
    return botRegex.test(userAgent);
  }

  init(
    configTrackerName?: string,
    configTrackingUrl?: string,
    configAppId?: string,
    newSnowplowTrackerFn = this.newTrackerFn,
  ) {
    if (!configTrackerName || !configTrackingUrl || !configAppId) {
      return;
    }

    this.tracker = newSnowplowTrackerFn(configTrackerName, this.forceHttps(configTrackingUrl), {
      appId: configAppId,
      encodeBase64: true,
      eventMethod: 'post',
      stateStorageStrategy: 'cookieAndLocalStorage',
      plugins: [BrowserFeaturesPlugin(), TimezonePlugin()],
    });
  }

  eventCategories() {
    const values = {
      VISITOR_EVENT: 'VisitorEvent',
      EXPERIMENT: 'Experiment',
    };

    return values;
  }

  snowplowEvent(
    eventCategory: string,
    eventName: string,
    schema: any,
    data: any,
    trackSnowplowStructEventFn = this.trackStructEventFn,
  ) {
    if (this.isBotUserAgent()) {
      return;
    }
    try {
      if (this.tracker) {
        trackSnowplowStructEventFn(this.tracker, eventCategory, eventName, data, schema);
      } else {
        trackSnowplowStructEventFn({
          category: eventCategory,
          action: eventName,
          context: this.contexts(data, schema),
        });
      }
    } catch (e) {
      console.error('Snowplow event error', e);
    }
  }

  // NOTE: This only works for browser trackers (not server side)
  pageView(customTitle: string, schema: any, data: any, trackSnowplowPageViewFn = trackPageView) {
    if (this.isBotUserAgent()) {
      return;
    }
    const search = this.metadataUtils.getWindowLocationSearch();
    if (search) {
      data.search = data.search || search;
    }
    trackSnowplowPageViewFn({ title: customTitle, context: this.contexts(data, schema) });
  }

  // Format OneTrust cookie data
  addOnetrustCookies(optanonConsent: any) {
    const onetrustInfo: any = {};

    if (optanonConsent && Object.keys(optanonConsent).length !== 0) {
      // Create JSON object from OneTrust cookie data that is formatted as a url search param string
      const oneTrustCookieData = Object.fromEntries(new URLSearchParams(optanonConsent));

      Object.entries(oneTrustCookieData).forEach(([key, value]) => {
        if (Object.keys(OptanonConsentCookies).includes(key)) {
          if (key === 'groups') {
            value.split(',').forEach(groupValue => {
              const [cookieType, optValue] = groupValue.split(':');
              const groups: any = OptanonConsentCookies.groups;
              if (Object.keys(groups).includes(cookieType)) {
                const groupCookieType = groups[cookieType];
                onetrustInfo[groupCookieType] = optValue === '1' ? 'opt_in' : 'opt_out';
              }
            });
          } else {
            onetrustInfo[(OptanonConsentCookies as any)[key]] = value;
          }
        }
      });
    }

    return onetrustInfo && Object.keys(onetrustInfo).length !== 0 ? onetrustInfo : null;
  }

  // Returns flattened object consists of properties, visitor_id, and user_id (if available)
  prepareData(
    userId: string,
    visitorId: string,
    properties: any,
    serverSideEligibleProviders = [],
    userProfileService: UserProfileService,
    userTargeting: UserTargeting,
    experimentation: Experimentation,
    adFraudDetection: AdFraudDetection,
  ) {
    const data = properties;

    // Add visitor_id
    data.visitor_id = visitorId;

    // Add user_id if available
    if (userId) {
      data.user_id = userId;
    }

    const onetrustCookieData = this.addOnetrustCookies(data.optanon_consent);
    if (onetrustCookieData) {
      data.onetrust = onetrustCookieData;
    }
    // Delete unformatted optanon_consent value from data
    // Intentionally commenting delete code to assit in Identity investigation where consent ID is missing
    // delete data.optanon_consent;

    const gafdId = adFraudDetection.getAdFraudDetectionId();
    const gexpId = experimentation.getExperimentationId();
    const upsId = userProfileService.getUserProfileServiceId();
    const userTargetId = userTargeting.getUserTargetingId();

    if (gafdId) {
      data.gafd_id = gafdId;
    }

    if (gexpId) {
      data.gexp_id = gexpId;
    }

    if (upsId) {
      data.ups_id = upsId;
    }

    if (userTargetId) {
      data.user_target_id = userTargetId;
    }

    data.server_side_eligible_providers = serverSideEligibleProviders;

    return data;
  }

  // Returns generated schema based on categoryName and eventName
  // schema = 'com.gusto/<categoryName + eventName>/<version> if hasData = true
  //        = 'com.gusto/noData/<version>' if hasData = false
  generateSchema(categoryName: string, eventName: string, hasData: boolean, version: number = 1) {
    const schemaName = hasData ? buildCamel(categoryName, eventName) : 'noData';
    return `com.gusto/${this.downcaseFirstLetter(schemaName)}/${version}`;
  }

  private contexts = (data: any, schema: any) =>
    data == null
      ? null
      : [
          {
            data,
            schema,
          },
        ];

  private downcaseFirstLetter = (str: string) => str[0].toLowerCase() + str.slice(1);
}
